import type { Landmark } from '../../../data/types';
import type { LinearRGB } from '../../../core/color';
import type { Ring, Vec2, Vec3 } from '../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { SURFACE } from '../../mesh/attributes';
import { beamSolid, boxSolid, hexahedronSolid, planPoint, prismSolid } from '../solids';
import { readNumber } from './params';
import {
  dominantAxis,
  meshFrameOf,
  planBounds,
  planFrameOf,
  toLayout as planToLayout,
  type PlanFrame,
} from './planFrame';
import type { PlanRect } from './genericBlock';
import { buildMarketGoods } from './marketGoods';

/** The painting's two continuous timber fish-market benches on the broad dam crest. */
export const MARKET = {
  /** Room to walk round the rows inside the market's ground. */
  margin: 2.2,
  /**
   * One row: a counter under two boards sloping to a ridge, the boards running the row's whole length
   * as the painting draws them, and a post at every division and at both ends.
   */
  row: {
    width: 3.4,
    tableY: 1.0,
    eaveY: 2.7,
    ridgeY: 3.8,
    postHalf: 0.09,
    boardHalf: 0.07,
    /** The boards run past the ridge and the posts into the counter, so no two parts share a face. */
    sink: 0.06,
    /** The feet stand under the ground, as the houses' do. */
    baseY: -0.25,
  },
  /** The divisions' spacing along a row. */
  stall: { length: 3.6, gap: 0.9 },
  /** The market's ground: a beaten-earth slab, `inset` m inside the rectangle, `topY` m proud of it. */
  paving: { inset: 0.4, topY: 0.03, bottomY: -0.3 },
} as const;

export interface MarketColors {
  /** The counters and the posts. */
  readonly timber: LinearRGB;
  /** The boards over them, a little darker. */
  readonly board: LinearRGB;
  /** The market's ground: beaten earth, the town's paving colour, as the painting draws it pale. */
  readonly ground: LinearRGB;
}

/** One place on the market ground: its row's v and its stretch along u. */
export interface MarketStall {
  readonly u: number;
  readonly v: number;
  readonly halfLength: number;
  readonly halfWidth: number;
}

/** One row of the market: the bench's ends, where its posts stand, and its own line. */
export interface MarketRow {
  readonly u0: number;
  readonly u1: number;
  readonly v: number;
  /** The divisions, left to right, each the near end of the place it begins. */
  readonly divisions: readonly number[];
  readonly halfWidth: number;
}

interface MarketPlan {
  readonly plan: PlanFrame;
  readonly frame: Frame;
  readonly rect: PlanRect;
}

export function marketPlan(landmark: Landmark): MarketPlan {
  const plan = planFrameOf(landmark.footprint, dominantAxis(landmark.footprint, landmark.heading));
  return { plan, frame: meshFrameOf(plan), rect: planBounds(plan) };
}

/** The market's geometry as its params give it: the rows across the ground and how long each runs. */
function marketRowsFor(rect: PlanRect, rows: number, span: number): MarketRow[] {
  const rowOffset = (rect.v1 - rect.v0) / 2 - MARKET.margin;
  const from = rect.u0 + MARKET.margin;
  const to = rect.u1 - MARKET.margin;
  /* The counter's whole length, its last place's own stretch included, inside the margin. */
  const count = Math.max(1, Math.floor((to - from + MARKET.stall.gap) / span));
  const counter = count * span - MARKET.stall.gap;
  const first = (from + to) / 2 - counter / 2;
  const out: MarketRow[] = [];
  for (let r = 0; r < rows; r++) {
    const v =
      (rows === 1 ? 0 : rowOffset - (2 * rowOffset * r) / (rows - 1)) + (rect.v0 + rect.v1) / 2;
    const divisions = Array.from({ length: count }, (_, i) => first + i * span);
    out.push({
      u0: divisions[0]!,
      u1: divisions[count - 1]! + MARKET.stall.length,
      v,
      divisions,
      halfWidth: MARKET.row.width / 2,
    });
  }
  return out;
}

/**
 * The places along the market's rows: `rows` rows evenly spread across the ground's width, each
 * divided `stallM + gapM` apart from margin to margin.
 */
export function marketStalls(landmark: Landmark, warn: (msg: string) => void): MarketStall[] {
  const { rect } = marketPlan(landmark);
  const rows = Math.max(1, Math.round(readNumber(landmark.params, 'rows', 2, warn)));
  const length = readNumber(landmark.params, 'stallM', MARKET.stall.length, warn);
  const span = length + readNumber(landmark.params, 'gapM', MARKET.stall.gap, warn);
  return marketRowsFor(rect, rows, span).flatMap((row) =>
    row.divisions.map((u) => ({
      u: u + length / 2,
      v: row.v,
      halfLength: length / 2,
      halfWidth: MARKET.row.width / 2,
    }))
  );
}

/**
 * Where each place of the market is traded at: the middle of both long sides of its counter, in the
 * layout, and the way out from the counter there.
 */
export function marketStallFronts(
  landmark: Landmark,
  warn: (msg: string) => void
): { readonly at: Vec2; readonly out: Vec2 }[] {
  const { plan } = marketPlan(landmark);
  return marketStalls(landmark, warn).flatMap((stall) =>
    [1, -1].map((side) => {
      const at = planToLayout(plan, [stall.u, stall.v + side * stall.halfWidth]);
      const beyond = planToLayout(plan, [stall.u, stall.v + side * (stall.halfWidth + 1)]);
      return { at, out: [beyond[0] - at[0], beyond[1] - at[1]] as Vec2 };
    })
  );
}

/** The rows' own lines: their ends, their line across the ground and their divisions. */
export function marketRows(landmark: Landmark, warn: (msg: string) => void): MarketRow[] {
  const rows = Math.max(1, Math.round(readNumber(landmark.params, 'rows', 2, warn)));
  const span =
    readNumber(landmark.params, 'stallM', MARKET.stall.length, warn) +
    readNumber(landmark.params, 'gapM', MARKET.stall.gap, warn);
  return marketRowsFor(marketPlan(landmark).rect, rows, span);
}

/** The market's ground: one slab of beaten earth over the rectangle the rows stand on. */
function buildPaving(builder: MeshBuilder, style: VertexStyle, frame: Frame, rect: PlanRect): void {
  const p = MARKET.paving;
  prismSolid(
    builder,
    style,
    frame,
    [
      [rect.u0 + p.inset, rect.v0 + p.inset],
      [rect.u1 - p.inset, rect.v0 + p.inset],
      [rect.u1 - p.inset, rect.v1 - p.inset],
      [rect.u0 + p.inset, rect.v1 - p.inset],
    ],
    p.bottomY,
    p.topY,
    { top: true, bottom: true }
  );
}

/**
 * One board of a row's awning: a slab from the eave at one side up to the ridge, running the row's
 * whole length. Its corners are two quads, the inner one `sink` past the ridge so the two boards meet
 * without a face between them, and the underside one board's half-thickness below the upper face.
 */
function awningBoard(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  row: MarketRow,
  side: -1 | 1
): void {
  const r = MARKET.row;
  const eave = planPoint(row.u0, r.eaveY, row.v + side * row.halfWidth);
  const ridge = planPoint(row.u0, r.ridgeY, row.v - side * r.sink);
  const along: Vec3 = [ridge[0] - eave[0], ridge[1] - eave[1], ridge[2] - eave[2]];
  const length = Math.hypot(along[1], along[2]);
  const t = r.boardHalf;
  /* The perpendicular to the board's slope that points down: the underside's way from the top face. */
  const candidates: readonly Vec3[] = [
    [0, along[2], -along[1]],
    [0, -along[2], along[1]],
  ];
  const unit = candidates.find((c) => c[1] < 0) ?? candidates[0]!;
  const down: Vec3 = [0, (unit[1] / length) * t, (unit[2] / length) * t];
  const run = row.u1 - row.u0;
  const forwards = (p: Vec3): Vec3 => [p[0] + run, p[1], p[2]];
  const under = (p: Vec3): Vec3 => [p[0] + down[0], p[1] + down[1], p[2] + down[2]];
  hexahedronSolid(builder, style, frame, [
    eave,
    forwards(eave),
    ridge,
    forwards(ridge),
    under(eave),
    forwards(under(eave)),
    under(ridge),
    forwards(under(ridge)),
  ]);
}

/**
 * One row: the counter the fish was laid on, the posts that divide it and carry the awning, and the
 * awning's two boards over it.
 */
function buildRow(
  builder: MeshBuilder,
  styles: { readonly timber: VertexStyle; readonly board: VertexStyle },
  frame: Frame,
  row: MarketRow
): void {
  const r = MARKET.row;
  boxSolid(
    builder,
    { ...styles.timber, extent: [row.u1 - row.u0, r.tableY - r.baseY] as const },
    frame,
    [row.u0, r.baseY, row.v - row.halfWidth],
    [row.u1, r.tableY, row.v + row.halfWidth],
    { top: true, bottom: true }
  );
  for (const u of [...row.divisions, row.u1]) {
    beamSolid(
      builder,
      styles.timber,
      frame,
      planPoint(u, r.baseY, row.v),
      planPoint(u, r.ridgeY + r.sink, row.v),
      r.postHalf
    );
  }
  for (const side of [-1, 1] as const) awningBoard(builder, styles.board, frame, row, side);
}

/** The market's rows: two benches under their awnings on the Vissersdam's open ground. */
export function buildMarketStalls(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: MarketColors,
  ids: { readonly edgeId: number; readonly seed: number },
  warn: (msg: string) => void,
  sharedGround = false,
  includeGoods = true
): void {
  const { frame: groundFrame, rect } = marketPlan(landmark);
  const baseY = readNumber(landmark.params, 'baseY', 0, warn);
  const frame: Frame = {
    ...groundFrame,
    origin: [groundFrame.origin[0], baseY, groundFrame.origin[2]],
  };
  const base = { ...ids, strokeAngle: 0, extent: [rect.u1 - rect.u0, MARKET.row.ridgeY] as const };
  const styles = {
    timber: { ...base, color: colors.timber, surface: SURFACE.wood },
    board: { ...base, color: colors.board, surface: SURFACE.wood },
    ground: {
      ...base,
      color: colors.ground,
      surface: SURFACE.street,
      extent: [rect.u1 - rect.u0, MARKET.row.ridgeY] as const,
    },
  };
  // The Dam already supplies the same beaten-earth surface under these benches.
  if (!sharedGround) buildPaving(builder, styles.ground, frame, rect);
  for (const row of marketRows(landmark, warn)) buildRow(builder, styles, frame, row);
  if (includeGoods && landmark.id === 'landmark-vismarkt')
    buildMarketGoods(builder, frame, marketStalls(landmark, warn), colors, base);
}

/** Only the benches block walking. The reserved landmark footprint keeps procedural lots clear. */
export function marketStallObstacles(landmark: Landmark): readonly Ring[] {
  const { plan } = marketPlan(landmark);
  const toLayout = (p: Vec2): Vec2 => {
    const c = Math.cos(plan.heading);
    const s = Math.sin(plan.heading);
    return [plan.origin[0] + p[0] * c - p[1] * s, plan.origin[1] + p[0] * s + p[1] * c];
  };
  const places = marketStalls(landmark, () => undefined).map((stall) =>
    (
      [
        [stall.u - stall.halfLength, stall.v - stall.halfWidth],
        [stall.u + stall.halfLength, stall.v - stall.halfWidth],
        [stall.u + stall.halfLength, stall.v + stall.halfWidth],
        [stall.u - stall.halfLength, stall.v + stall.halfWidth],
      ] as Ring
    ).map(toLayout)
  );
  return places;
}
